import type { Locale } from './languageStore'

/**
 * URL-slug convention for locale-prefixed routes — separate from both the
 * `Locale` type's hyphenated-capitalized values ('zh-Hans'/'zh-Hant', used
 * throughout the store/UI) and `ContentTranslation`'s camelCase keys
 * (zhHans/zhHant, used in content JSON). Lowercase is the standard URL
 * convention; this module is the single place that translates between all
 * three so nothing else needs to know more than one of them.
 */
const LOCALE_SLUGS: Partial<Record<Locale, string>> = {
  'zh-Hans': 'zh-hans',
  'zh-Hant': 'zh-hant',
}

const SLUG_TO_LOCALE: Record<string, Locale> = Object.fromEntries(
  Object.entries(LOCALE_SLUGS).map(([locale, slug]) => [slug, locale as Locale]),
)

/** Every locale that has a real prefixed URL — English deliberately excluded, it lives at the unprefixed path. */
export const PREFIXED_LOCALES = Object.keys(LOCALE_SLUGS) as Locale[]

export const ALL_LOCALES: Locale[] = ['en', ...PREFIXED_LOCALES]

export function localeToSlug(locale: Locale): string | undefined {
  return LOCALE_SLUGS[locale]
}

export function slugToLocale(slug: string): Locale | undefined {
  return SLUG_TO_LOCALE[slug]
}

/** Strips a leading /zh-hans or /zh-hant segment, if present, returning the locale-agnostic path (always starting with "/"). */
export function stripLocalePrefix(pathname: string): string {
  const [, maybeSlug, ...rest] = pathname.split('/')
  if (maybeSlug && slugToLocale(maybeSlug)) {
    const stripped = '/' + rest.join('/')
    return stripped === '/' ? '/' : stripped.replace(/\/$/, '') || '/'
  }
  return pathname
}

/**
 * Builds a localized app-route URL and normalizes every non-file route to
 * the trailing-slash form Cloudflare serves canonically. Query strings and
 * hashes are preserved after the normalized pathname.
 */
export function localizedPath(locale: Locale, path: string): string {
  const suffixIndex = path.search(/[?#]/)
  const rawPath = suffixIndex === -1 ? path : path.slice(0, suffixIndex)
  const suffix = suffixIndex === -1 ? '' : path.slice(suffixIndex)
  const agnostic = stripLocalePrefix(rawPath)
  const normalized = agnostic === '/' ? '/' : `${agnostic.replace(/\/+$/, '')}/`
  const slug = localeToSlug(locale)
  if (!slug) return `${normalized}${suffix}`
  return normalized === '/' ? `/${slug}/${suffix}` : `/${slug}${normalized}${suffix}`
}

const SITE_URL = 'https://hellokaleido.net'

/**
 * Absolute URL for `path` (locale-agnostic, e.g. "/explore/eiffel-tower/")
 * in the given locale — mirrors scripts/prerender.mjs's/
 * generate-sitemap.mjs's localizedUrl, which build the same URLs at build
 * time for the prerendered shells and sitemap.
 */
export function localizedUrl(locale: Locale, path: string): string {
  return `${SITE_URL}${localizedPath(locale, path)}`
}

/**
 * The fixed 4-entry hreflang alternate set (self + both siblings +
 * x-default→en) for one page family, given its locale-agnostic path — used
 * by useDocumentMeta to keep client-side navigation's hreflang links
 * correct. Every prerendered page ships this same set already (see
 * scripts/prerender.mjs's matching alternatesFor); this is what recomputes
 * it after e.g. a "nearby discovery" swaps which place is showing.
 */
export function buildAlternates(path: string): { hreflang: string; href: string }[] {
  const links: { hreflang: string; href: string }[] = ALL_LOCALES.map((locale) => ({
    hreflang: locale,
    href: localizedUrl(locale, path),
  }))
  links.push({ hreflang: 'x-default', href: localizedUrl('en', path) })
  return links
}
